import { useCallback } from 'react'
import { createFetchWebEventCollector, EventName, PageName } from 'web-event-collector'
import { getEnv } from '@unni/platform'

import { createCommonEvent } from '@/utils/eventCollector'

import type { User } from '@/features/member/member.type'
import type { HospitalView } from '@/features/hospital/types/hospital.type'

/**
 * Hospital detail analytics hook
 * Manages all analytics tracking for hospital detail interactions
 */
export const useHospitalAnalytics = (user: User | null, hospital: HospitalView) => {
  // Track doctor click events
  const trackDoctorClick = useCallback(
    (doctorId: number) => {
      if (user && user.id !== 0) {
        const commonEvent = createCommonEvent(navigator.userAgent, user.id)
        const { emitDoctorClicked } = createFetchWebEventCollector({
          baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
        })

        emitDoctorClicked({
          ...commonEvent,
          Name: EventName.DoctorClicked,
          Body: {
            ...commonEvent.Body,
            doctorId,
            hospitalId: hospital.hospitalId,
            pageName: PageName.HospitalDetailWeb,
          },
        })
      }
    },
    [user, hospital.hospitalId],
  )

  // Track event click events
  const trackEventClick = useCallback(
    (eventId: number) => {
      if (user && user.id !== 0) {
        const commonEvent = createCommonEvent(navigator.userAgent, user.id)
        // Event tracking logic will be expanded here
        console.log('Event clicked:', { eventId, hospitalId: hospital.hospitalId, userId: user.id })
      }
    },
    [user, hospital.hospitalId],
  )

  // Track section visibility (for scroll-based analytics)
  const trackSectionView = useCallback(
    (sectionName: string, duration?: number) => {
      if (user && user.id !== 0) {
        console.log('Section viewed:', {
          sectionName,
          duration,
          hospitalId: hospital.hospitalId,
          userId: user.id,
        })
      }
    },
    [user, hospital.hospitalId],
  )

  // Track filter/tab interactions
  const trackFilterInteraction = useCallback(
    (filterType: string, filterValue: string) => {
      if (user && user.id !== 0) {
        console.log('Filter interaction:', {
          filterType,
          filterValue,
          hospitalId: hospital.hospitalId,
          userId: user.id,
        })
      }
    },
    [user, hospital.hospitalId],
  )

  // Track tab click events
  const trackTabClick = useCallback(
    (targetId: string) => {
      if (user && user.id !== 0) {
        console.log('Tab clicked:', {
          targetId,
          hospitalId: hospital.hospitalId,
          userId: user.id,
        })
      }
    },
    [user, hospital.hospitalId],
  )

  // Track more list click events
  const trackMoreListClick = useCallback(
    (target: string) => {
      if (user && user.id !== 0) {
        console.log('More list clicked:', {
          target,
          hospitalId: hospital.hospitalId,
          userId: user.id,
        })
      }
    },
    [user, hospital.hospitalId],
  )

  // Track copy address events
  const trackCopyAddress = useCallback(() => {
    if (user && user.id !== 0) {
      console.log('Address copied:', {
        hospitalId: hospital.hospitalId,
        userId: user.id,
      })
    }
  }, [user, hospital.hospitalId])

  // Track map click events
  const trackMapClick = useCallback(() => {
    if (user && user.id !== 0) {
      console.log('Map clicked:', {
        hospitalId: hospital.hospitalId,
        userId: user.id,
      })
    }
  }, [user, hospital.hospitalId])

  // Track consultation click events
  const trackConsultationClick = useCallback(() => {
    if (user && user.id !== 0) {
      console.log('Consultation clicked:', {
        hospitalId: hospital.hospitalId,
        userId: user.id,
      })
    }
  }, [user, hospital.hospitalId])

  return {
    trackDoctorClick,
    trackEventClick,
    trackSectionView,
    trackFilterInteraction,
    trackTabClick,
    trackMoreListClick,
    trackCopyAddress,
    trackMapClick,
    trackConsultationClick,
  }
}
